import { TrashIcon } from '@heroicons/react/24/outline';

const ContentRemovalPolicyComp = () => {
    return (
        <div className="min-h-screen flex justify-center items-start">
            <div className="m-5 w-full max-w-3xl bg-gradient-to-br from-black/70 to-purple-900/40 rounded-3xl shadow-2xl p-6 md:p-10 border border-red-700/40 backdrop-blur-md animate-fade-in transition-all">
                <div className="flex flex-col items-center mb-8">
                    <TrashIcon className="h-10 w-10 text-red-400 mb-2" />
                    <h1 className="text-center text-2xl md:text-3xl font-bold text-white tracking-wide mb-1">ErotixAI Content Removal Policy</h1>
                    <div className="flex flex-col items-center text-center text-xs md:text-sm text-red-200 space-y-1">
                        <span>Last Updated: <span className="font-semibold">June 24, 2025</span></span>
                    </div>
                </div>
                <div className="prose prose-invert max-w-none text-white/90 text-sm md:text-base leading-relaxed">
                    <section>
                        <h2 className="text-lg font-semibold text-red-300 mb-1">1. Purpose</h2>
                        <p>ErotixAI provides AI-generated adult content based on user prompts. While we strive to deliver creative freedom and personalization, we are also committed to ensuring that content generated on our platform remains ethical, legal, and respectful.</p>
                        <p>This policy outlines how AI-generated content can be reported and removed, how we review requests, and our safeguards to prevent misuse.</p>
                    </section>
                    <section>
                        <h2 className="text-lg font-semibold text-red-300 mb-1">2. Nature of Content on ErotixAI</h2>
                        <ul className="list-disc list-inside space-y-1">
                            <li>All output on ErotixAI is AI-generated based on user prompts.</li>
                            <li>Users do not upload their own adult content; instead, they interact with the AI system.</li>
                            <li>Our AI is filtered to prevent illegal, unethical, or policy-violating content, but no system is perfect—so removal requests are vital.</li>
                        </ul>
                    </section>
                    <section>
                        <h2 className="text-lg font-semibold text-red-300 mb-1">3. Grounds for Content Removal</h2>
                        <ul className="list-disc list-inside space-y-1">
                            <li>Depicts non-consensual, abusive, or exploitative scenarios</li>
                            <li>Involves or implies minors or underage content, even fictionally</li>
                            <li>Includes hate speech, discrimination, or harassment</li>
                            <li>Violates intellectual property rights (e.g., impersonation of real individuals, unauthorized likeness)</li>
                            <li>Breaches local or international law</li>
                            <li>Violates ErotixAI's Community Guidelines or Terms of Service</li>
                        </ul>
                    </section>
                    <section>
                        <h2 className="text-lg font-semibold text-red-300 mb-1">4. How to Report or Request Removal</h2>
                        <p>If you come across inappropriate AI-generated content or misuse of the system, you can:</p>
                        <ul className="list-disc list-inside space-y-1">
                            <li>Use the in-app "Report" function directly from the content or chat interface</li>
                            <li>Email us: <a href="mailto:support@erotixai.com" className="underline">support@erotixai.com</a></li>
                            <li>Submit a form: <a href="https://erotixai.com/contact-us" className="underline">erotixai.com/report</a></li>
                        </ul>
                        <p>Please include:</p>
                        <ul className="list-disc list-inside space-y-1">
                            <li>The prompt used (if known)</li>
                            <li>Screenshot or text copy of the generated output</li>
                            <li>Reason for the complaint</li>
                            <li>Whether you're a user, a third party, or someone impersonated</li>
                        </ul>
                    </section>
                    <section>
                        <h2 className="text-lg font-semibold text-red-300 mb-1">5. Review Process and Timelines</h2>
                        <ul className="list-disc list-inside space-y-1">
                            <li>Acknowledgment: Within 48 hours</li>
                            <li>All reported complaints will be resolved within 7 business days</li>
                            <li>Content Removal or Restriction: If found violating, content will be disabled or blocked from regeneration</li>
                        </ul>
                        <p>If the complaint involves impersonation, deepfake likeness, or legal risk, we may request verification and act with urgency.</p>
                    </section>
                    <section>
                        <h2 className="text-lg font-semibold text-red-300 mb-1">6. Appeals</h2>
                        <p>If your prompt or generated content was restricted or removed and you believe this was in error:</p>
                        <ul className="list-disc list-inside space-y-1">
                            <li>You can submit an appeal via email at: <a href="mailto:appeals@erotixai.com" className="underline">appeals@erotixai.com</a></li>
                            <li>Appeals will be reviewed within 5 calendar days</li>
                        </ul>
                        <p>Note: Reinstated content may still be monitored or flagged for sensitive themes.</p>
                    </section>
                    <section>
                        <h2 className="text-lg font-semibold text-red-300 mb-1">7. Limitations & User Responsibility</h2>
                        <p>While ErotixAI provides filtered AI experiences, users are ultimately responsible for how they interact with the system. Prompting the AI to generate harmful or policy-violating content is prohibited and may result in:</p>
                        <ul className="list-disc list-inside space-y-1">
                            <li>Warnings</li>
                            <li>Temporary suspension</li>
                            <li>Permanent account bans</li>
                        </ul>
                    </section>
                    <section>
                        <h2 className="text-lg font-semibold text-red-300 mb-1">8. Privacy & Anonymity</h2>
                        <p>All removal requests are confidential. We do not disclose complainant identities unless legally required. Any content tied to personally identifiable data will be reviewed with special care.</p>
                    </section>
                    <section>
                        <h2 className="text-lg font-semibold text-red-300 mb-1">9. AI Filtering and Ongoing Moderation</h2>
                        <p>Our AI models are equipped with filters and safety layers to prevent most harmful outputs. However, we also rely on:</p>
                        <ul className="list-disc list-inside space-y-1">
                            <li>User reporting</li>
                            <li>Human moderation</li>
                            <li>Ongoing audits to refine our safety systems</li>
                        </ul>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default ContentRemovalPolicyComp; 